// src/components/ChatInput.tsx

import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import { Mic, Paperclip, ArrowUp } from "lucide-react";
import { useDictation } from "../hooks/useDictation";
import type { RealtimeVoiceHandle } from "../hooks/useRealtimeVoice";

const VoiceWaveIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 12h2" />
    <path d="M7 8v8" />
    <path d="M11 5v14" />
    <path d="M15 8v8" />
    <path d="M19 12h2" />
  </svg>
);

const RealtimeVoiceOrb = () => (
  <span
    className="
      relative block h-9 w-9 overflow-hidden rounded-full
      bg-gradient-to-br from-blue-600 via-blue-300 to-white
      shadow-[0_0_12px_rgba(59,130,246,0.35)]
      animate-[pulse_2.4s_ease-in-out_infinite]
      motion-reduce:animate-none
    "
    aria-hidden
  >
    {/* Soft white cloud */}
    <span
      className="
        absolute -left-2 top-1
        h-5 w-10 rounded-full
        bg-white/80 blur-md
        animate-[spin_4s_linear_infinite]
        motion-reduce:animate-none
      "
    />

    {/* Moving blue cloud */}
    <span
      className="
        absolute -right-3 bottom-0
        h-6 w-10 rounded-full
        bg-blue-500/65 blur-md
        animate-[spin_5s_linear_infinite]
        motion-reduce:animate-none
      "
      style={{ animationDirection: "reverse" }}
    />

    {/* Glassy surface */}
    <span
      className="
        absolute inset-[2px]
        rounded-full
        border border-white/60
      "
    />
  </span>
);

interface ChatInputProps {
  onSendMessage: (
    message: string,
    payload: { type: "text" | "audio"; dictationOrigin: boolean }
  ) => void;
  isLoading: boolean;
  isInitializing?: boolean;
  accessCode: string | null;
  sessionId: string;
  prefillText?: string;
  onClearPrefill?: () => void;
  sudsMode?: boolean;
  homeworkSelectMode?: boolean;
  disabled?: boolean;

  /**
   * Realtime voice handle (owned by ChatContainer).
   * If null/undefined, ChatInput behaves exactly like before (no wave/stop).
   */
  realtimeVoice?: Pick<
    RealtimeVoiceHandle,
    "supported" | "isActive" | "isConnecting" | "start" | "stop"
  > | null;
}

export interface ChatInputHandle {
  focus: () => void;
  insert: (text: string) => void;
  getMessage: () => string;
}

const WORD_TO_DIGIT: Record<string, string> = {
  zero: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
  ten: "10",
};

function normalizeNumericWord(value: string): string {
  const trimmed = value.trim().toLowerCase();
  return WORD_TO_DIGIT[trimmed] ?? value;
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  (
    {
      onSendMessage,
      isLoading,
      isInitializing = false,
      prefillText,
      onClearPrefill,
      sudsMode: sudsModeProp = false,
      homeworkSelectMode: homeworkSelectModeProp = false,
      disabled = false,

      // 🔊 Provided by ChatContainer (single source of truth)
      realtimeVoice = null,
    },
    ref
  ) => {
    // ----------------------------------------------------------
    // INTERNAL STATE
    // ----------------------------------------------------------
    const [message, setMessage] = useState("");
    const [dictationOrigin, setDictationOrigin] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const dictationModeRef = useRef<number | null>(null);

    const [sudsMode, setSudsMode] = useState(sudsModeProp);
    const [homeworkSelectMode, setHomeworkSelectMode] =
      useState(homeworkSelectModeProp);

    useEffect(() => setSudsMode(sudsModeProp), [sudsModeProp]);
    useEffect(
      () => setHomeworkSelectMode(homeworkSelectModeProp),
      [homeworkSelectModeProp]
    );

    // ----------------------------------------------------------
    // RESET INPUT ON MODE CHANGES
    // ----------------------------------------------------------
    useEffect(() => {
      setMessage("");
      setDictationOrigin(false);
      requestAnimationFrame(autoExpand);
    }, [sudsModeProp, homeworkSelectModeProp]);

    const prevDisabledRef = useRef(false);
    useEffect(() => {
      const nowDisabled = isLoading || disabled || isInitializing;
      const wasDisabled = prevDisabledRef.current;
      prevDisabledRef.current = nowDisabled;

      if (wasDisabled && !nowDisabled && !isInitializing) {
        setMessage("");
        setDictationOrigin(false);
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    }, [isLoading, disabled, isInitializing]);

    // ----------------------------------------------------------
    // IMPERATIVE API
    // ----------------------------------------------------------
    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
      insert: (text: string) => {
        setMessage(text);
        setDictationOrigin(false);
        requestAnimationFrame(() => textareaRef.current?.focus());
      },
      getMessage: () => message,
    }));

    // ----------------------------------------------------------
    // PREFILL
    // ----------------------------------------------------------
    useEffect(() => {
      if (isInitializing) return;
      if (prefillText) {
        setMessage(prefillText);
        setDictationOrigin(false);
        textareaRef.current?.focus();
        onClearPrefill?.();
      }
    }, [prefillText, isInitializing, onClearPrefill]);

    // ----------------------------------------------------------
    // DICTATION
    // ----------------------------------------------------------
    const { transcript, isListening, toggle } = useDictation();

    useEffect(() => {
      if (!transcript || isInitializing) return;
      if (transcript.sessionId !== dictationModeRef.current) return;

      let text = transcript.text;
      if (sudsMode || homeworkSelectMode) {
        text = normalizeNumericWord(text);
      }

      setMessage(text);
      setDictationOrigin(true);
      requestAnimationFrame(autoExpand);
    }, [transcript, isInitializing, sudsMode, homeworkSelectMode]);

    // ----------------------------------------------------------
    // HELPERS
    // ----------------------------------------------------------
    const autoExpand = () => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };

    // ----------------------------------------------------------
    // SUBMIT
    // ----------------------------------------------------------
    const handleSubmit = (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      if (disabled || isLoading || isInitializing) return;

      const trimmed = message.trim();
      if (!trimmed) return;

      // ✅ safety: stop realtime capture on typed submit
      if (realtimeVoice?.isActive) {
        realtimeVoice.stop?.();
      }

      onSendMessage(trimmed, {
        type:
          localStorage.getItem("efachat.audioPlayback") === "true"
            ? "audio"
            : "text",
        dictationOrigin,
      });

      setMessage("");
      setDictationOrigin(false);
      setSudsMode(false);
      requestAnimationFrame(autoExpand);
    };

    // ----------------------------------------------------------
    // PRIMARY ACTION STATE (Wave ↔ Send ↔ Stop)
    // ----------------------------------------------------------
    const hasText = message.trim().length > 0;

    const voiceEnabled =
      Boolean(realtimeVoice?.supported) && !isLoading && !isInitializing && !disabled;

    const isVoiceActive = Boolean(realtimeVoice?.isActive);

    const isVoiceConnecting = Boolean(realtimeVoice?.isConnecting);

    // stop > send > startVoice
    const actionMode: "stop" | "send" | "startVoice" = isVoiceActive
      ? "stop"
      : hasText
        ? "send"
        : voiceEnabled
          ? "startVoice"
          : "send";

    const isStop = actionMode === "stop";
    const isSend = actionMode === "send";
    const isStartVoice = actionMode === "startVoice";

    // ----------------------------------------------------------
    // RENDER
    // ----------------------------------------------------------
    return (
      <form className="flex flex-col gap-2" onSubmit={handleSubmit}>
        <div className="flex items-center gap-2">
          {/* File (disabled/locked as before) */}
          <label
            className="flex items-center justify-center h-[42px] w-[42px] rounded-lg bg-gray-100 text-gray-300 cursor-not-allowed"
            onClick={(e) => e.preventDefault()}
          >
            <Paperclip className="w-5 h-5" />
          </label>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setDictationOrigin(false);
              requestAnimationFrame(autoExpand);
            }}
            onKeyDown={(e) => {
              // Avoid breaking IME composition (Japanese/Chinese/etc.)
              const composing = (e.nativeEvent as any)?.isComposing;
              if (composing) return;

              // Enter -> send (like ChatGPT). Shift+Enter -> newline.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            rows={1}
            placeholder="Type your message or use dictation…"
            readOnly={isLoading || isInitializing || disabled}
            className="
            flex-1 px-4 py-2
            text-base
            border border-gray-300 rounded-lg
            resize-none overflow-hidden
            focus:outline-none
            focus:ring-0
            focus:border-gray-300
            appearance-none
          "
          />

          {/* Dictation mic (unchanged) */}
          <button
            type="button"
            onClick={() => {
              const sid = toggle();
              if (typeof sid === "number") dictationModeRef.current = sid;
            }}
            disabled={isLoading || isInitializing || disabled}
            className={`h-[42px] w-[42px] rounded-lg flex items-center justify-center ${isListening
              ? "bg-red-500 text-white"
              : "bg-gray-200 hover:bg-gray-300"
              }`}
          >
            <Mic className="w-5 h-5" />
          </button>

          {/* PRIMARY ACTION (Stop ↔ Send ↔ Wave) */}
          <button
            type={isSend ? "submit" : "button"}
            aria-label={
              isStop ? "Stop voice" : isStartVoice ? "Start voice" : "Send message"
            }
            disabled={
              !isStop &&
              (disabled || isLoading || isInitializing || isVoiceConnecting)
            }
            onClick={() => {
              if (
                !isStop &&
                (disabled || isLoading || isInitializing || isVoiceConnecting)
              ) return;

              if (isStop) {
                realtimeVoice?.stop?.();
                return;
              }

              if (isStartVoice) {
                realtimeVoice?.start?.();
                return;
              }

              // isSend -> submit handles it (Enter + button)
            }}
            className={`h-[42px] w-[42px] flex items-center justify-center transition-all ${isSend
              ? "rounded-lg bg-green-500 text-white hover:bg-green-600"
              : isStop
                ? "rounded-full bg-white hover:bg-blue-50"
                : "rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300"
              }`}
          >
            {isStop ? (
              <RealtimeVoiceOrb />
            ) : isSend ? (
              <ArrowUp className="w-5 h-5" />
            ) : (
              <VoiceWaveIcon />
            )}
          </button>
        </div>
      </form>
    );
  }
);

export default ChatInput;
